import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useModals } from "@/lib/modal-context";

const NAV_LINKS = [
    { label: "HOME", id: "home", to: "/" },
    { label: "SERVICES", id: "services", to: "/#services", dropdown: true },
    { label: "PROJECTS", id: "projects", to: "/#projects" },
    { label: "ABOUT US", id: "about", to: "/#about" },
    { label: "CONTACT", id: "contact", to: "/contact-us" },
];

const SERVICES = [
    { label: "Office Fit-Out Dubai", to: "/office-fit-out-dubai" },
    { label: "Home Interiors Dubai", to: "/home-interiors-dubai" },
    { label: "Retail Branding Dubai", to: "/retail-branding-dubai" },
    { label: "Restaurant Interiors Dubai", to: "/restaurant-interiors-dubai" },
];

const WhatsIcon = ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
        <path d="M19.05 4.91A9.83 9.83 0 0 0 12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.74.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02Z"/>
    </svg>
);

export default function Navbar() {
    const { openConnect } = useModals();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isActive = (link) => {
        if (link.id === "home") return location.pathname === "/";
        if (link.id === "services") return location.pathname.startsWith("/home-interiors") || location.pathname.startsWith("/office") || location.pathname.startsWith("/retail") || location.pathname.startsWith("/restaurant");
        return false;
    };

    return (
        <nav
            data-testid="main-navbar"
            className={`sticky top-0 z-40 w-full bg-white transition-shadow ${scrolled ? "shadow-sm" : ""}`}
        >
            <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8 py-3">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
                    <img
                        src="https://customer-assets.emergentagent.com/job_dream-design-deliver/artifacts/mfputtlc_45DaySpaces.com%20Logo.webp"
                        alt="45DaySpaces"
                        className="h-11 sm:h-12 w-auto object-contain"
                    />
                </Link>

                {/* Desktop links */}
                <div className="hidden lg:flex items-center gap-1">
                    {NAV_LINKS.map((link) => (
                        <div
                            key={link.id}
                            className="relative"
                            onMouseEnter={() => link.dropdown && setServicesOpen(true)}
                            onMouseLeave={() => link.dropdown && setServicesOpen(false)}
                        >
                            <Link
                                to={link.to}
                                data-testid={`nav-link-${link.id}`}
                                className={`inline-flex items-center gap-1 px-4 py-2 text-[12px] font-bold tracking-[0.12em] rounded-full transition-colors ${
                                    isActive(link)
                                        ? "bg-[#CCFF00] text-black"
                                        : "text-black/80 hover:text-black"
                                }`}
                            >
                                {link.label}
                                {link.dropdown && <ChevronDown className="h-3 w-3" />}
                            </Link>
                            {link.dropdown && servicesOpen && (
                                <div className="absolute left-0 top-full pt-2 z-50">
                                    <div className="w-64 rounded-2xl border border-black/10 bg-white shadow-xl p-2">
                                        {SERVICES.map((s) => (
                                            <Link
                                                key={s.label}
                                                to={s.to}
                                                onClick={() => setServicesOpen(false)}
                                                className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-[#FFFDE8] text-[13px] font-semibold text-black/80 hover:text-black"
                                            >
                                                {s.label}
                                                {s.note && <span className="text-[10px] font-bold text-[#7e9a00] uppercase">Soon</span>}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* CTA — 20% smaller */}
                <button
                    onClick={() => openConnect()}
                    data-testid="nav-cta-connect"
                    className="hidden md:inline-flex btn-pill btn-lime text-[9px] pl-3 pr-1 py-1"
                >
                    <span className="tracking-[0.08em]">CONNECT WITH OUR SPECIALIST IN 15 SECS</span>
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                        <WhatsIcon className="h-3 w-3" />
                    </span>
                </button>

                {/* Mobile menu */}
                <button
                    onClick={() => setMobileOpen((s) => !s)}
                    className="lg:hidden p-2 -mr-2"
                    data-testid="nav-mobile-toggle"
                    aria-label="menu"
                >
                    {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </div>

            {mobileOpen && (
                <div className="lg:hidden border-t border-black/10 bg-white px-5 pb-4">
                    <div className="flex flex-col gap-1 pt-3">
                        {NAV_LINKS.map((l) => (
                            <Link
                                key={l.id}
                                to={l.to}
                                onClick={() => setMobileOpen(false)}
                                className={`rounded-full px-4 py-2 text-sm font-bold tracking-wider ${isActive(l) ? "bg-[#CCFF00]" : ""}`}
                            >
                                {l.label}
                            </Link>
                        ))}
                        <div className="mt-2 pt-2 border-t border-black/10">
                            <div className="text-[10px] font-bold text-black/50 px-4 mb-1">SERVICES</div>
                            {SERVICES.map((s) => (
                                <Link
                                    key={s.label}
                                    to={s.to}
                                    onClick={() => setMobileOpen(false)}
                                    className="block px-4 py-2 text-sm font-semibold text-black/70"
                                >
                                    {s.label}
                                </Link>
                            ))}
                        </div>
                        <button
                            onClick={() => { setMobileOpen(false); openConnect(); }}
                            className="btn-pill btn-lime mt-2 justify-center"
                        >
                            CONNECT IN 15 SECS <WhatsIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </nav>
    );
}
